import { useState } from 'react';
import { Package, Search, ExternalLink, CheckCircle2, XCircle, AlertTriangle, Link2, Loader2, RefreshCw, Sparkles, Eye, ShieldCheck, Edit3, Upload, Download, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';

const CATEGORIES = [
  'insulation', 'solar', 'heat_pumps', 'batteries', 'boilers',
  'ventilation', 'thermostats', 'glazing', 'small_measures',
];

const STATUS_BADGE = {
  draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
  flagged: { label: 'Flagged', cls: 'bg-orange-100 text-orange-700' },
};

const LINK_BADGE = {
  no_link: { label: 'No link', cls: 'bg-muted text-muted-foreground' },
  link_pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700' },
  linked: { label: 'Linked', cls: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Link Live', cls: 'bg-green-100 text-green-700' },
};

const SUGGESTION_BADGE = {
  no_link: { label: 'No suggestion', cls: 'bg-muted text-muted-foreground' },
  suggestion_found: { label: 'Suggestion found', cls: 'bg-purple-100 text-purple-700' },
  pending_review: { label: 'Pending review', cls: 'bg-amber-100 text-amber-700' },
  verified_link: { label: 'Verified', cls: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};

const CONFIDENCE_BADGE = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-red-100 text-red-700',
};

function SuggestionPanel({ product, onUpdateSuggestion, onSuggestLink }) {
  const [editing, setEditing] = useState(false);
  const [editLink, setEditLink] = useState(product.suggested_link || '');
  const [editRetailer, setEditRetailer] = useState(product.suggested_retailer || '');
  const [editNotes, setEditNotes] = useState(product.suggestion_verification_notes || '');
  const [finalLink, setFinalLink] = useState(product.suggested_link || '');
  const [suggesting, setSuggesting] = useState(false);

  const ss = product.suggestion_status || 'no_link';
  const hasSuggestion = ss !== 'no_link' && ss !== 'rejected';
  const sb = SUGGESTION_BADGE[ss] || SUGGESTION_BADGE.no_link;

  const handleSuggest = async () => {
    setSuggesting(true);
    try {
      await onSuggestLink(product.product_id);
      toast.success('Link suggestion agent started');
    } catch { toast.error('Failed to start suggestion'); }
    finally { setSuggesting(false); }
  };

  const handleSave = async (action) => {
    try {
      await onUpdateSuggestion(product.product_id, action, {
        suggested_link: editLink,
        suggested_retailer: editRetailer,
        verification_notes: editNotes,
        affiliate_link: finalLink,
      });
      setEditing(false);
      toast.success(`Suggestion ${action.replace(/_/g, ' ')}`);
    } catch { toast.error('Update failed'); }
  };

  return (
    <div className="border-t border-purple-200 bg-purple-50/30 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="w-4 h-4 text-purple-600" />
          <h5 className="text-xs font-semibold uppercase tracking-wider text-purple-700">Affiliate Link Suggestion</h5>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sb.cls}`}>{sb.label}</span>
        </div>
        {!hasSuggestion && (
          <button onClick={handleSuggest} disabled={suggesting}
            className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
            data-testid={`suggest-link-${product.product_id}`}>
            {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Suggest Link
          </button>
        )}
      </div>

      {hasSuggestion && (
        <div className="space-y-2">
          {/* Suggestion details */}
          <div className="grid md:grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-muted-foreground">Retailer:</span>
              <span className="ml-1 font-medium">{product.suggested_retailer || '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Relevance:</span>
              <span className="ml-1 font-medium capitalize">{(product.suggestion_relevance || '—').replace(/_/g, ' ')}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Confidence:</span>
              <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${CONFIDENCE_BADGE[product.suggestion_confidence] || ''}`}>
                {product.suggestion_confidence || '—'}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Has affiliate program:</span>
              <span className="ml-1 font-medium">{product.suggestion_has_affiliate_program ? 'Yes' : 'No / Unknown'}</span>
            </div>
          </div>

          {/* Source evidence */}
          <div className="text-xs space-y-1 bg-white/50 rounded-md p-2">
            <p className="text-muted-foreground">
              <span className="font-semibold">Source:</span> {product.suggestion_source_name || '—'}
            </p>
            {product.suggested_link && (
              <a href={product.suggested_link} target="_blank" rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-0.5 break-all">
                {product.suggested_link} <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            )}
            <p className="text-muted-foreground">
              <span className="font-semibold">Checked:</span> {product.suggestion_checked_date ? new Date(product.suggestion_checked_date).toLocaleString() : '—'}
            </p>
            {product.suggestion_verification_notes && (
              <p className="text-muted-foreground italic">{product.suggestion_verification_notes}</p>
            )}
          </div>

          {product.suggestion_reasoning && (
            <p className="text-xs text-muted-foreground bg-white/50 rounded-md p-2">
              <span className="font-semibold">Reasoning:</span> {product.suggestion_reasoning}
            </p>
          )}

          {/* Edit mode */}
          {editing ? (
            <div className="space-y-2 bg-white rounded-md p-3 border border-border">
              <div>
                <label className="text-[10px] text-muted-foreground uppercase">Retailer</label>
                <input value={editRetailer} onChange={e => setEditRetailer(e.target.value)}
                  className="w-full text-xs border border-border rounded px-2 py-1.5 mt-0.5" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground uppercase">Suggested URL</label>
                <input value={editLink} onChange={e => setEditLink(e.target.value)}
                  className="w-full text-xs border border-border rounded px-2 py-1.5 mt-0.5" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground uppercase">Final Affiliate URL</label>
                <input value={finalLink} onChange={e => setFinalLink(e.target.value)}
                  placeholder="Paste the actual affiliate tracking URL here"
                  className="w-full text-xs border border-border rounded px-2 py-1.5 mt-0.5" />
              </div>
              <div>
                <label className="text-[10px] text-muted-foreground uppercase">Verification Notes</label>
                <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={2}
                  className="w-full text-xs border border-border rounded px-2 py-1.5 mt-0.5" />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={() => handleSave('verified_link')}
                  className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> Mark Verified
                </button>
                <button onClick={() => handleSave('approved')}
                  className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1"
                  data-testid={`approve-suggestion-${product.product_id}`}>
                  <CheckCircle2 className="w-3 h-3" /> Approve & Go Live
                </button>
                <button onClick={() => setEditing(false)}
                  className="px-3 py-1.5 text-xs bg-muted text-foreground rounded-md">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* Action buttons */
            <div className="flex gap-2 flex-wrap pt-1">
              {ss === 'suggestion_found' && (
                <button onClick={() => handleSave('pending_review')}
                  className="px-3 py-1.5 text-xs bg-amber-600 text-white rounded-md hover:bg-amber-700 flex items-center gap-1">
                  <Eye className="w-3 h-3" /> Start Review
                </button>
              )}
              <button onClick={() => { setEditing(true); setEditLink(product.suggested_link || ''); setEditRetailer(product.suggested_retailer || ''); setEditNotes(product.suggestion_verification_notes || ''); setFinalLink(product.suggested_link || ''); }}
                className="px-3 py-1.5 text-xs bg-muted text-foreground rounded-md hover:bg-muted/80 flex items-center gap-1"
                data-testid={`edit-suggestion-${product.product_id}`}>
                <Edit3 className="w-3 h-3" /> Edit & Verify
              </button>
              <button onClick={() => handleSave('rejected')}
                className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-1">
                <XCircle className="w-3 h-3" /> Reject
              </button>
              <button onClick={handleSuggest} disabled={suggesting}
                className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1">
                {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Re-suggest
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProductImportPanel({ token, onDone }) {
  const [csvText, setCsvText] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [allowOverwrite, setAllowOverwrite] = useState(false);
  const [open, setOpen] = useState(false);

  const opts = { headers: { Cookie: `access_token=${token}` }, withCredentials: true };

  const submitText = async () => {
    if (!csvText.trim()) return toast.error('Paste CSV content first');
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/affiliate-products/import`,
        { csv_text: csvText, allow_link_overwrite: allowOverwrite },
        opts
      );
      setResult(data);
      toast.success(`Imported: ${data.created} created, ${data.updated} updated`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    }
    setBusy(false);
  };

  const submitFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(
        `${API}/admin/affiliate-products/import-file?allow_link_overwrite=${allowOverwrite}`,
        fd,
        { ...opts, headers: { ...opts.headers, 'Content-Type': 'multipart/form-data' } }
      );
      setResult(data);
      toast.success(`Imported: ${data.created} created, ${data.updated} updated`);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    }
    setBusy(false);
  };

  return (
    <div className="bg-white rounded-xl border border-blue-200 shadow-sm" data-testid="product-import-panel">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between p-4 hover:bg-blue-50/50"
        data-testid="toggle-product-import-btn"
      >
        <span className="flex items-center gap-2">
          <Upload className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-semibold">Import products CSV</span>
          <span className="text-[11px] text-muted-foreground">(bulk affiliate links · imported rows do not auto-activate)</span>
        </span>
        <span className="text-xs text-blue-600">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <a
              href={`${API}/admin/affiliate-products/import-template.csv`}
              className="px-3 py-1.5 text-xs bg-muted text-foreground rounded hover:bg-muted/80 flex items-center gap-1"
              download
              data-testid="download-product-template-btn"
            >
              <Download className="w-3 h-3" /> Template CSV
            </a>
            <label className="flex items-center gap-1 text-[11px] text-muted-foreground ml-auto cursor-pointer">
              <input
                type="checkbox"
                checked={allowOverwrite}
                onChange={(e) => setAllowOverwrite(e.target.checked)}
                data-testid="allow-overwrite-checkbox"
              />
              <span>Allow overwrite of approved links (when product_id matches)</span>
            </label>
          </div>

          <textarea
            value={csvText}
            onChange={(e) => setCsvText(e.target.value)}
            rows={6}
            placeholder="Paste CSV content here (header row required) — or use file upload below"
            className="w-full text-xs border border-border rounded p-2 font-mono"
            data-testid="product-csv-textarea"
          />

          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={submitText}
              disabled={busy}
              className="px-3 py-1.5 text-xs bg-primary text-white rounded hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
              data-testid="import-product-paste-btn"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
              Import paste
            </button>

            <label
              className="px-3 py-1.5 text-xs bg-white border border-blue-300 text-blue-700 rounded hover:bg-blue-50 cursor-pointer flex items-center gap-1"
              data-testid="import-product-file-btn"
            >
              <Upload className="w-3 h-3" /> Upload CSV file
              <input
                type="file"
                accept=".csv,text/csv,text/plain"
                className="hidden"
                onChange={(e) => submitFile(e.target.files?.[0])}
              />
            </label>

            {result && (
              <span className="text-[11px] text-muted-foreground ml-auto">
                Last: {result.created} created · {result.updated} updated · {result.protected} protected · {result.errors?.length || 0} errors
              </span>
            )}
          </div>

          {result && (
            <div className="bg-muted/30 rounded p-2 text-[11px] space-y-1" data-testid="product-import-result">
              <div className="flex flex-wrap gap-3">
                <span><strong>{result.created}</strong> created</span>
                <span><strong>{result.updated}</strong> updated</span>
                <span><strong>{result.protected}</strong> link-protected</span>
                <span><strong>{result.errors?.length || 0}</strong> errors</span>
                {result.status_breakdown && Object.entries(result.status_breakdown).map(([s, n]) => (
                  <span key={s} className="text-muted-foreground">{s}: <strong>{n}</strong></span>
                ))}
              </div>
              {(result.errors || []).length > 0 && (
                <details className="text-[10px]">
                  <summary className="cursor-pointer text-muted-foreground">Show errors ({result.errors.length})</summary>
                  <ul className="mt-1 space-y-0.5 max-h-40 overflow-auto">
                    {result.errors.slice(0, 50).map((e, i) => (
                      <li key={i} className={e.reason?.startsWith('protected_skip') ? 'text-amber-700' : 'text-red-700'}>
                        {e.row_number > 0 ? `row ${e.row_number}: ` : ''}{e.reason}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function AffiliateProductsTab({ token, products, onApprove, onReject, onUpdateLink, onResearch, onReviewCheck, onBatchReview, onSuggestLink, onSuggestBatch, onUpdateSuggestion, onImported }) {
  const [catFilter, setCatFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [linkFilter, setLinkFilter] = useState('');
  const [researching, setResearching] = useState(false);
  const [batchChecking, setBatchChecking] = useState(false);
  const [batchSuggesting, setBatchSuggesting] = useState(false);
  const [selectedCat, setSelectedCat] = useState('heat_pumps');
  const [batchCat, setBatchCat] = useState('');
  const [batchLimit, setBatchLimit] = useState(10);
  const [suggestCat, setSuggestCat] = useState('');
  const [suggestLimit, setSuggestLimit] = useState(10);
  const [expandedId, setExpandedId] = useState(null);

  const filtered = products.filter(p => {
    if (catFilter && p.category !== catFilter) return false;
    if (statusFilter && p.status !== statusFilter) return false;
    if (linkFilter === 'no_link' && (p.link_status === 'linked' || p.link_status === 'approved' || p.suggestion_status === 'suggestion_found' || p.suggestion_status === 'pending_review' || p.suggestion_status === 'verified_link')) return false;
    if (linkFilter === 'has_suggestion' && !['suggestion_found', 'pending_review', 'verified_link'].includes(p.suggestion_status)) return false;
    if (linkFilter === 'linked' && p.link_status !== 'linked' && p.link_status !== 'approved') return false;
    return true;
  });

  const handleResearch = async () => {
    setResearching(true);
    try {
      await onResearch(selectedCat);
      toast.success(`Research started for ${selectedCat.replace(/_/g, ' ')}`);
    } catch { toast.error('Failed to start research'); }
    finally { setResearching(false); }
  };

  const handleBatchReview = async () => {
    setBatchChecking(true);
    try {
      const result = await onBatchReview(batchCat || null, batchLimit);
      toast.success(`Batch review started: ${result.products_queued} product(s) queued`);
    } catch { toast.error('Failed to start batch review'); }
    finally { setBatchChecking(false); }
  };

  const handleBatchSuggest = async () => {
    setBatchSuggesting(true);
    try {
      const result = await onSuggestBatch(suggestCat || null, suggestLimit);
      toast.success(`Link suggestion started: ${result.products_queued} product(s) queued`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to start suggestions'); }
    finally { setBatchSuggesting(false); }
  };

  const noLinkCount = products.filter(p => p.status === 'approved' && p.link_status !== 'linked' && p.link_status !== 'approved' && !['suggestion_found', 'pending_review', 'verified_link'].includes(p.suggestion_status)).length;
  const suggestedCount = products.filter(p => ['suggestion_found', 'pending_review', 'verified_link'].includes(p.suggestion_status)).length;

  return (
    <div className="space-y-4" data-testid="affiliate-products-tab">
      {/* CSV import (mass affiliate-link onboarding) */}
      <ProductImportPanel token={token} onDone={onImported} />

      {/* Research trigger */}
      <div className="bg-white rounded-xl border border-border p-4 shadow-sm">
        <h3 className="text-sm font-semibold mb-3">Research Products</h3>
        <div className="flex gap-2 items-end flex-wrap">
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Category</label>
            <select value={selectedCat} onChange={e => setSelectedCat(e.target.value)}
              className="text-sm border border-border rounded-lg px-3 py-2 bg-white" data-testid="research-category-select">
              {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
          <button onClick={handleResearch} disabled={researching}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
            data-testid="research-btn">
            {researching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
            Research
          </button>
        </div>
      </div>

      {/* Batch actions row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Batch review */}
        <div className="bg-white rounded-xl border border-border p-4 shadow-sm">
          <h3 className="text-sm font-semibold mb-3">Batch Review Check</h3>
          <div className="flex gap-2 items-end flex-wrap">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Category</label>
              <select value={batchCat} onChange={e => setBatchCat(e.target.value)}
                className="text-sm border border-border rounded-lg px-3 py-2 bg-white" data-testid="batch-cat-select">
                <option value="">All approved</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Limit</label>
              <input type="number" value={batchLimit} onChange={e => setBatchLimit(Math.min(25, Math.max(1, parseInt(e.target.value) || 1)))}
                min={1} max={25} className="text-sm border border-border rounded-lg px-3 py-2 bg-white w-20" data-testid="batch-limit-input" />
            </div>
            <button onClick={handleBatchReview} disabled={batchChecking}
              className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="batch-review-btn">
              {batchChecking ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Check Reviews
            </button>
          </div>
        </div>

        {/* Batch link suggestions */}
        <div className="bg-white rounded-xl border border-purple-200 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <h3 className="text-sm font-semibold">Suggest Affiliate Links</h3>
            {noLinkCount > 0 && (
              <span className="text-[10px] bg-purple-100 text-purple-700 rounded-full px-2 py-0.5 font-medium">{noLinkCount} need links</span>
            )}
          </div>
          <div className="flex gap-2 items-end flex-wrap">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Category</label>
              <select value={suggestCat} onChange={e => setSuggestCat(e.target.value)}
                className="text-sm border border-border rounded-lg px-3 py-2 bg-white" data-testid="suggest-cat-select">
                <option value="">Highest clicks first</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Limit</label>
              <input type="number" value={suggestLimit} onChange={e => setSuggestLimit(Math.min(20, Math.max(1, parseInt(e.target.value) || 1)))}
                min={1} max={20} className="text-sm border border-border rounded-lg px-3 py-2 bg-white w-20" data-testid="suggest-limit-input" />
            </div>
            <button onClick={handleBatchSuggest} disabled={batchSuggesting}
              className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              data-testid="batch-suggest-btn">
              {batchSuggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Suggest Links
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          className="text-xs border border-border rounded-md px-2 py-1 bg-white" data-testid="product-cat-filter">
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-xs border border-border rounded-md px-2 py-1 bg-white" data-testid="product-status-filter">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="flagged">Flagged</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={linkFilter} onChange={e => setLinkFilter(e.target.value)}
          className="text-xs border border-border rounded-md px-2 py-1 bg-white" data-testid="link-filter">
          <option value="">All link statuses</option>
          <option value="no_link">No link / no suggestion</option>
          <option value="has_suggestion">Has suggestion</option>
          <option value="linked">Linked / Live</option>
        </select>
        <span className="text-xs text-muted-foreground self-center">{filtered.length} product(s)</span>
        {suggestedCount > 0 && (
          <span className="text-xs text-purple-600 self-center ml-2">{suggestedCount} suggestions awaiting review</span>
        )}
      </div>

      {/* Product cards */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-border p-8 text-center">
          <Package className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No products match the current filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(p => {
            const sb = STATUS_BADGE[p.status] || STATUS_BADGE.draft;
            const lb = LINK_BADGE[p.link_status] || LINK_BADGE.no_link;
            const cb = CONFIDENCE_BADGE[p.confidence] || CONFIDENCE_BADGE.low;
            const ss = SUGGESTION_BADGE[p.suggestion_status] || null;
            const expanded = expandedId === p.product_id;
            return (
              <div key={p.product_id} className={`bg-white rounded-xl border shadow-sm overflow-hidden ${p.status === 'flagged' ? 'border-orange-300' : p.suggestion_status === 'suggestion_found' ? 'border-purple-200' : 'border-border'}`}
                data-testid={`product-${p.product_id}`}>
                <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expanded ? null : p.product_id)}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="font-medium text-sm">{p.product_name}</h4>
                        <span className="text-xs text-muted-foreground">{p.manufacturer}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium uppercase ${p.tier === 'best' ? 'bg-green-100 text-green-700' : p.tier === 'better' ? 'bg-blue-100 text-blue-700' : 'bg-muted text-muted-foreground'}`}>{p.tier}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {p.category.replace(/_/g, ' ')} · €{p.retail_price_eur?.toLocaleString()} · {p.review_score ? `${p.review_score}/5` : 'no score'} {p.review_count ? `(${p.review_count} reviews)` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${cb}`}>{p.confidence}</span>
                      {ss && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${ss.cls}`}>{ss.label}</span>}
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${lb.cls}`}>{lb.label}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${sb.cls}`}>{sb.label}</span>
                    </div>
                  </div>
                  {p.status === 'flagged' && p.flagged_reason && (
                    <div className="mt-2 flex items-start gap-1.5 text-xs text-orange-700 bg-orange-50 rounded-md p-2">
                      <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      {p.flagged_reason}
                    </div>
                  )}
                </div>

                {expanded && (
                  <div className="border-t border-border p-4 bg-muted/30 space-y-3">
                    {/* Source evidence */}
                    <div>
                      <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Source Evidence</h5>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Source:</span>
                        <span className="font-medium">{p.source_name || 'Not provided'}</span>
                        {p.source_url && (
                          <a href={p.source_url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-0.5">
                            Verify <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      {p.notes && <p className="text-xs text-muted-foreground mt-1 italic">{p.notes}</p>}
                      <p className="text-xs text-muted-foreground mt-1">Researched: {new Date(p.researched_at).toLocaleString()}</p>
                    </div>

                    {/* Strengths / weaknesses */}
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <h5 className="text-xs font-semibold text-green-700 mb-1">Strengths</h5>
                        <ul className="space-y-0.5">{p.strengths?.map((s, i) => <li key={i} className="text-xs text-green-700">+ {s}</li>)}</ul>
                      </div>
                      <div>
                        <h5 className="text-xs font-semibold text-red-700 mb-1">Weaknesses</h5>
                        <ul className="space-y-0.5">{p.weaknesses?.map((w, i) => <li key={i} className="text-xs text-red-700">- {w}</li>)}</ul>
                      </div>
                    </div>

                    {/* Product actions */}
                    <div className="flex gap-2 flex-wrap pt-2 border-t border-border">
                      {p.status === 'draft' && (
                        <>
                          <button onClick={() => onApprove(p.product_id)} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1" data-testid={`approve-${p.product_id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Approve
                          </button>
                          <button onClick={() => onReject(p.product_id, 'Not suitable')} className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-1" data-testid={`reject-${p.product_id}`}>
                            <XCircle className="w-3 h-3" /> Reject
                          </button>
                        </>
                      )}
                      {p.status === 'flagged' && (
                        <button onClick={() => onApprove(p.product_id)} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3" /> Re-approve
                        </button>
                      )}
                      <button onClick={() => onReviewCheck(p.product_id)} className="px-3 py-1.5 text-xs bg-muted text-foreground rounded-md hover:bg-muted/80 flex items-center gap-1" data-testid={`check-review-${p.product_id}`}>
                        <Search className="w-3 h-3" /> Check Reviews
                      </button>
                    </div>

                    {/* Affiliate link suggestion panel */}
                    {p.status === 'approved' && (
                      <SuggestionPanel product={p} onUpdateSuggestion={onUpdateSuggestion} onSuggestLink={onSuggestLink} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
